/**
 * Feb 2026 — Session stability fix.
 *
 * ─── Qué cambió respecto a la versión anterior ──────────────────────────────
 *   • El 401 interceptor YA NO borra el token ante cualquier 401. Solo lo
 *     borra cuando el backend manda uno de los `INVALID_TOKEN_DETAILS` reales
 *     (ver /app/backend/security.py — strings copiados LITERALMENTE).
 *   • Errores de red / timeout / 502-503-504 se reintentan 2 veces con
 *     backoff (500ms + 1500ms) de forma transparente. Nunca cierran sesión.
 *   • Al confirmarse que el token está muerto, se cancelan todas las peticiones
 *     en vuelo (polls de notificaciones, pending-counts, etc.) para evitar la
 *     cascada de 401 que antes trababa la UI.
 *   • Los componentes pueden escuchar `window.addEventListener("hittcg:session-killed")`
 *     para hacer un redirect limpio al login.
 */
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API_BASE = `${BACKEND_URL}/api`;

// ─── 1. Strings EXACTOS de "detail" que mandan los 401 reales. ──────────────
// Copiados literalmente de /app/backend/security.py (líneas 89, 94, 96, 100, 115).
// Cualquier otro 401 se trata como ambiguo (race, endpoint-específico, hiccup)
// y NO desloguea.
const INVALID_TOKEN_DETAILS = new Set([
  "No autenticado",
  "Token expirado",
  "Token inválido",
  "Usuario no encontrado",
  "Sesión cerrada por cambio de contraseña",
]);

// ─── 2. Códigos transitorios reintentables. ────────────────────────────────
const RETRYABLE_STATUS = new Set([502, 503, 504]);
const RETRYABLE_AXIOS_CODES = new Set(["ERR_NETWORK", "ECONNABORTED", "ETIMEDOUT"]);
const MAX_RETRIES = 2;
const RETRY_DELAYS_MS = [500, 1500]; // backoff para los 2 intentos

const sleep = (ms) => new Promise((r) => setTimeout(r, ms));

// ─── 3. AbortController compartido por sesión. ─────────────────────────────
// Cuando se confirma un token muerto, abortamos TODAS las peticiones en vuelo
// (polls de NotificationsBell, admin counts, admin loaders) para que no se
// encadenen más 401s que confundan la UI.
let sessionAbort = new AbortController();

export function cancelInflightOnSessionKill() {
  const previous = sessionAbort;
  sessionAbort = new AbortController(); // una fresca para la próxima sesión
  try { previous.abort(); } catch { /* noop */ }
}

export const api = axios.create({
  baseURL: API_BASE,
  timeout: 30000, // 30s absoluto; antes era infinito
});

// ─── 4. Request interceptor: Authorization + signal compartido. ────────────
api.interceptors.request.use((config) => {
  const token = localStorage.getItem("hittcg_token");
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  // Si el caller NO pasó su propio signal, enganchamos el signal compartido
  // de sesión. Así una muerte de sesión cancela todas las peticiones en vuelo.
  if (!config.signal) {
    config.signal = sessionAbort.signal;
  }
  return config;
});

// ─── 5. Response interceptor: retry → luego decidir si logout o propagar. ──
api.interceptors.response.use(
  (r) => r,
  async (err) => {
    const config = err.config || {};
    const status = err.response?.status;
    const axiosCode = err.code;

    // Caller pidió explícitamente NO reintentar (p.ej. refresh() en auth.jsx
    // maneja sus propios retries para evitar compound-retry).
    const noRetry = config.__noRetry === true;

    // ─── A. Retry transitorios ────────────────────────────────────────────
    // Reintentables: 502/503/504, Network Error (sin response), timeouts.
    const hasResponse = !!err.response;
    const transient =
      RETRYABLE_STATUS.has(status) ||
      RETRYABLE_AXIOS_CODES.has(axiosCode) ||
      (!hasResponse && !axios.isCancel(err) && axiosCode !== "ERR_CANCELED");

    const retriesSoFar = config.__retryCount ?? 0;
    if (!noRetry && transient && retriesSoFar < MAX_RETRIES) {
      config.__retryCount = retriesSoFar + 1;
      await sleep(RETRY_DELAYS_MS[retriesSoFar] ?? 1500);
      // Recrear la request. Si sessionAbort ya fue abortado, fallará rápido.
      return api(config);
    }

    // ─── B. 401 handling: solo matar sesión en strings EXACTOS. ───────────
    if (status === 401) {
      const detail = err.response?.data?.detail;
      const detailStr = typeof detail === "string" ? detail : null;
      if (detailStr && INVALID_TOKEN_DETAILS.has(detailStr)) {
        const path = window.location.pathname;
        if (!path.startsWith("/login") && !path.startsWith("/register")) {
          localStorage.removeItem("hittcg_token");
          sessionStorage.setItem("hittcg_logout_reason", detailStr);
          cancelInflightOnSessionKill();
          window.dispatchEvent(
            new CustomEvent("hittcg:session-killed", { detail: detailStr })
          );
        }
      }
      // 401 "ambiguo" (p.ej. un endpoint específico que falló por race, un
      // bug puntual del servidor, un bearer que llegó malformado por un
      // middleware intermedio): NO borramos token, propagamos tal cual.
    }

    // ─── C0. Origen caído (deploy): Cloudflare devuelve HTML crudo. Nunca lo mostramos. ───
    if (status >= 500 && typeof err.response?.data === "string") {
      err.response.data = { detail: "Estamos actualizando HitTCG, vuelve en un momento…" };
    }

    // ─── C. Normalizar FastAPI 422 (idéntico al código anterior). ─────────
    if (err.response?.data?.detail && Array.isArray(err.response.data.detail)) {
      err.response.data.detail = err.response.data.detail
        .map((d) => {
          if (typeof d === "string") return d;
          const field = Array.isArray(d?.loc) ? d.loc.filter((x) => x !== "body").join(".") : "";
          const msg = d?.msg || "Valor inválido";
          return field ? `${field}: ${msg}` : msg;
        })
        .join(" · ");
    } else if (err.response?.data?.detail && typeof err.response.data.detail !== "string") {
      err.response.data.detail = JSON.stringify(err.response.data.detail);
    }
    return Promise.reject(err);
  }
);

// ─── Helpers de imagen (sin cambios respecto a la versión anterior) ────────
export async function fetchPrivateImage(path, width) {
  const token = localStorage.getItem("hittcg_token");
  const qs = width ? `?w=${width}` : "";
  const url = `${API_BASE}/files/private/${path}${qs}`;
  const res = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
  if (!res.ok) return null;
  const blob = await res.blob();
  return URL.createObjectURL(blob);
}

export function publicImageUrl(path, width) {
  const qs = width ? `?w=${width}` : "";
  return `${API_BASE}/files/public/${path}${qs}`;
}

export function cardImageUrl(url, width) {
  if (!url) return null;
  if (typeof url !== "string") return null;
  if (url.startsWith("http://") || url.startsWith("https://") || url.startsWith("blob:") || url.startsWith("data:")) return url;
  if (url.startsWith("/api/")) return `${API_BASE.replace(/\/api$/, "")}${url}`;
  return publicImageUrl(url, width);
}

export function cardName(card, language) {
  if (!card) return "—";
  if (card.name) return card.name;
  const key = language === "en" ? "name_en"
            : language === "ja" ? "name_ja"
            : "name_es";
  return card[key] || card.name_es || card.name_en || card.name_ja || "—";
}
